import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useStoryStore } from '../stores/storyStore';
import StudentNameForm from '../components/StudentNameForm';
import StoryQuestion from '../components/StoryQuestion';
import StoryComplete from '../components/StoryComplete';

const { FiBook } = FiIcons;

const StudentStoryPage = () => {
  const { shareId } = useParams();
  const { getStoryByShareId } = useStoryStore();
  const [story, setStory] = useState(null);
  const [studentName, setStudentName] = useState('');
  const [currentNodeId, setCurrentNodeId] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [completionType, setCompletionType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const foundStory = getStoryByShareId(shareId);
    if (foundStory) {
      setStory(foundStory);
      setCurrentNodeId('start');
    } else {
      setError('Geschichte nicht gefunden oder nicht veröffentlicht');
    }
    setLoading(false);
  }, [shareId, getStoryByShareId]);

  const handleNameSubmit = (name) => {
    setStudentName(name);
  };

  const handleAnswerSubmit = (nextNodeId) => {
    if (!nextNodeId) return;

    const nextNode = story.nodes.find(node => node.id === nextNodeId);
    if (nextNode) {
      if (nextNode.data.type === 'end') {
        setIsComplete(true);
        setCompletionType(nextNode.data.isSuccess ? 'success' : 'failure');
      } else {
        setCurrentNodeId(nextNodeId);
      }
    }
  };

  const handleRestart = () => {
    setCurrentNodeId('start');
    setIsComplete(false);
    setCompletionType(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#2b6777' }}></div>
          <p className="mt-4 font-rubik" style={{ color: '#2b6777' }}>Geschichte wird geladen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <SafeIcon icon={FiBook} className="w-16 h-16 mx-auto mb-4" style={{ color: '#9ca3af' }} />
          <h1 className="text-2xl font-bold mb-2 font-rubik" style={{ color: '#2b6777' }}>Geschichte nicht gefunden</h1>
          <p className="font-rubik" style={{ color: '#2b6777' }}>{error}</p>
        </div>
      </div>
    );
  }

  const currentNode = story.nodes.find(node => node.id === currentNodeId);

  return (
    <div 
      className="min-h-screen"
      style={{ 
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)'
      }}
    >
      <div className="container mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          {!studentName ? (
            <StudentNameForm
              key="name-form"
              storyTitle={story.title}
              onSubmit={handleNameSubmit}
            />
          ) : isComplete ? (
            <StoryComplete
              key="complete"
              studentName={studentName}
              storyTitle={story.title}
              isSuccess={completionType === 'success'}
              onRestart={handleRestart}
            />
          ) : currentNode ? (
            <StoryQuestion
              key={currentNodeId}
              node={currentNode}
              studentName={studentName}
              onAnswer={handleAnswerSubmit}
            />
          ) : (
            <div className="text-center">
              <p className="font-rubik" style={{ color: '#2b6777' }}>Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.</p>
              <button
                onClick={handleRestart}
                className="mt-4 px-6 py-2 rounded transition-colors text-white font-rubik"
                style={{ backgroundColor: '#c8d8e4' }}
              >
                Geschichte neu starten
              </button>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default StudentStoryPage;